// src/pages/Permits.tsx — Permit to Work management
// Conventions from frontend-skill.md:
//   - Functional components with hooks only
//   - Tailwind only — no custom CSS
//   - Primary: indigo-600, Success: green-500, Warning: amber-500, Danger: red-500
//   - Page wrapper: <div className="p-6 space-y-6">
//   - Use StatusBadge, Modal components
//   - TierGuard for role-based UI
//
// WHSO (B_PM or above) — can approve/reject permits
// Field (D_FIELD, C_PROCUREMENT) — can submit permit applications
// All tiers — can view permits for their scoped jobs

import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_PERMITS, PERMIT_TYPE_META, Permit, PermitStatus, PermitType,
  MOCK_INDUCTION_MODULES,
} from '../utils/mockDataWHS'
import { MOCK_SWMS } from '../utils/mockDataTier3'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { fetchPermits } from '../services/permitsService'
import {
  FileText, Plus, Search, ArrowLeft, X, CheckCircle, Clock,
  AlertTriangle, ShieldCheck, Users, MapPin, Calendar, Link,
  ChevronRight, Lock, Eye,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// STATUS META
// ─────────────────────────────────────────────────────────────────
const STATUS_META: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  DRAFT:       { label: 'Draft',       bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400' },
  SUBMITTED:   { label: 'Submitted',   bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  APPROVED:    { label: 'Approved',    bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
  IN_PROGRESS: { label: 'In Progress', bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  COMPLETED:   { label: 'Completed',   bg: 'bg-slate-100',  text: 'text-slate-600',  dot: 'bg-slate-400' },
  REJECTED:    { label: 'Rejected',    bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500' },
  EXPIRED:     { label: 'Expired',     bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
}

function StatusBadge({ status }: { status: PermitStatus }) {
  const m = STATUS_META[status]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// CREDENTIAL CHECKER
// Checks worker credentials against permit type requirements
// In production: this queries WorkerInduction + Employee models via API
// ─────────────────────────────────────────────────────────────────
function CredentialBadge({ status, missing }: { status: string; missing: string[] }) {
  if (status === 'OK') return (
    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
      <CheckCircle className="w-3 h-3" /> Verified
    </span>
  )
  return (
    <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
      <AlertTriangle className="w-3 h-3" /> {missing.join(', ')} missing
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// PERMIT CARD (list view)
// ─────────────────────────────────────────────────────────────────
function PermitCard({ permit, onClick }: { permit: Permit; onClick: () => void }) {
  const meta = PERMIT_TYPE_META[permit.type]
  return (
    <div
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-indigo-200 cursor-pointer transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg} text-xl`}>
            {meta.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm">{permit.permitNumber}</span>
              <StatusBadge status={permit.status} />
            </div>
            <p className={`text-sm font-medium mt-0.5 ${meta.color}`}>{meta.label}</p>
            <p className="text-xs text-slate-500 mt-1 truncate">{permit.zone}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{permit.jobNumber}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(permit.plannedStart)}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{permit.workers.length} worker{permit.workers.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
      </div>

      {/* Credential warning */}
      {permit.workers.some(w => w.credentialStatus !== 'OK') && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>One or more workers have missing/expired credentials for this permit type</span>
        </div>
      )}

      {/* Awaiting approval banner */}
      {permit.status === 'SUBMITTED' && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2">
          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Awaiting WHSO approval · Submitted by {permit.submittedByName}</span>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// PERMIT DETAIL VIEW
// ─────────────────────────────────────────────────────────────────
function PermitDetail({
  permit: initial, onBack, onApprove, onReject, onStartWork, onCompleteWork,
  approvalChain, onAddApproval,
}: {
  permit: Permit
  onBack: () => void
  onApprove: (id: string) => void
  onReject: (id: string, reason: string) => void
  onStartWork: (id: string) => void
  onCompleteWork: (id: string, notes: string) => void
  approvalChain: { name: string; role: string; signedAt: string; tier: string }[]
  onAddApproval: (name: string, role: string, tier: string) => void
}) {
  const { isTier, user } = useAuthStore()
  const [permit, setPermit] = useState(initial)
  const [tab, setTab] = useState<'overview' | 'workers' | 'prestart' | 'closeout'>('overview')
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [closeOutNotes, setCloseOutNotes] = useState('')
  const [preStartItems, setPreStartItems] = useState(permit.preStartItems)

  const canApprove   = (isTier('B_PM') || isTier('C_WHSO')) && permit.status === 'SUBMITTED'
  const canStartWork = permit.status === 'APPROVED'
  const canCloseOut  = permit.status === 'IN_PROGRESS'
  const meta         = PERMIT_TYPE_META[permit.type]

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'workers',  label: `Workers (${permit.workers.length})` },
    { id: 'prestart', label: 'Pre-start' },
    { id: 'closeout', label: 'Close-out' },
  ]

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to permits
      </button>
      <button onClick={() => generatePermitPDF(permit)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
        Print permit PDF
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta.bg} text-2xl flex-shrink-0`}>
              {meta.icon}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{permit.permitNumber}</h1>
                <StatusBadge status={permit.status} />
              </div>
              <p className={`font-semibold mt-0.5 ${meta.color}`}>{meta.label}</p>
              <p className="text-sm text-slate-500 mt-1">{permit.zone}</p>
            </div>
          </div>
        </div>

        {/* Key info row */}
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><span className="text-slate-400 block text-xs">Job</span><span className="font-medium text-slate-900">{permit.jobNumber}</span></div>
          <div><span className="text-slate-400 block text-xs">Planned start</span><span className="font-medium text-slate-900">{formatDate(permit.plannedStart)}</span></div>
          <div><span className="text-slate-400 block text-xs">Planned end</span><span className="font-medium text-slate-900">{formatDate(permit.plannedEnd)}</span></div>
          <div><span className="text-slate-400 block text-xs">Submitted by</span><span className="font-medium text-slate-900">{permit.submittedByName}</span></div>
        </div>

        {/* Linked SWMS */}
        {permit.linkedSwmsId && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Link className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-500">Linked SWMS:</span>
            <span className="font-medium text-indigo-600">{permit.linkedSwmsNumber}</span>
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Approved
            </span>
          </div>
        )}

        {/* BYDA / Energex references */}
        {permit.bydaReference && (
          <div className="mt-2 text-sm text-slate-500">
            <span className="font-medium text-slate-700">BYDA reference:</span> {permit.bydaReference}
          </div>
        )}
        {permit.energexReference && (
          <div className="mt-1 text-sm text-slate-500">
            <span className="font-medium text-slate-700">Switching programme (SP):</span> {permit.energexReference}
          </div>
        )}

        {/* Atmospheric readings (confined space) */}
        {permit.type === 'CONFINED_SPACE' && permit.o2Level !== undefined && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-600 mb-2">Atmospheric Test Readings</p>
            <div className="grid grid-cols-4 gap-3 text-xs">
              {[
                { label: 'O₂', value: `${permit.o2Level}%`, ok: permit.o2Level >= 19.5 && permit.o2Level <= 23.5 },
                { label: 'CO', value: `${permit.coLevel} ppm`, ok: (permit.coLevel ?? 0) < 25 },
                { label: 'CH₄', value: `${permit.methaneLevel}% LEL`, ok: (permit.methaneLevel ?? 0) < 10 },
                { label: 'H₂S', value: `${permit.h2sLevel} ppm`, ok: (permit.h2sLevel ?? 0) < 1 },
              ].map(r => (
                <div key={r.label} className="text-center">
                  <div className={`text-lg font-bold ${r.ok ? 'text-green-600' : 'text-red-600'}`}>{r.value}</div>
                  <div className="text-slate-500">{r.label}</div>
                  <div className={`text-xs mt-0.5 ${r.ok ? 'text-green-600' : 'text-red-600'}`}>{r.ok ? '✓ OK' : '✗ FAIL'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {canApprove && (
            <>
              <button
                onClick={() => onApprove(permit.id)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <CheckCircle className="w-4 h-4" /> Approve permit
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg border border-red-200 transition-colors"
              >
                <X className="w-4 h-4" /> Reject
              </button>
            </>
          )}
          {canStartWork && (
            <button
              onClick={() => onStartWork(permit.id)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <ShieldCheck className="w-4 h-4" /> Start work (pre-start signed)
            </button>
          )}
          {canCloseOut && (
            <button
              onClick={() => setTab('closeout')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Lock className="w-4 h-4" /> Close out permit
            </button>
          )}
        </div>

        {/* Approval info */}
        {permit.approvedByName && (
          <div className="mt-3 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5" />
            Approved by {permit.approvedByName} · {formatDate(permit.approvedAt!)}
          </div>
        )}

        {/* Multi-approver chain */}
        {(permit.status === 'APPROVED' || permit.status === 'IN_PROGRESS') && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
              Approval chain — {[
                'ENERGISED_ELECTRICAL', 'OVERHEAD_LINES',
              ].includes(permit.type) ? '3 required (WHSO → PM → Authorised Person)' : '2 required (WHSO → PM)'}
            </h3>

            {/* Render existing chain entries */}
            <div className="space-y-2 mb-3">
              {/* Seed first approval from permit.approvedByName */}
              {permit.approvedByName && (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-900">{permit.approvedByName}</span>
                    <span className="text-slate-500 ml-2">— WHSO / Primary approver</span>
                  </div>
                  <span className="text-slate-400">{formatDate(permit.approvedAt!)}</span>
                </div>
              )}

              {/* Dynamic chain entries */}
              {approvalChain.map((a, i) => (
                <div key={i} className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-900">{a.name}</span>
                    <span className="text-slate-500 ml-2">— {a.role}</span>
                  </div>
                  <span className="text-slate-400">{new Date(a.signedAt).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                </div>
              ))}
            </div>

            {/* Pending chain slots */}
            {(() => {
              const needsPMSignoff = approvalChain.every(a => a.tier !== 'B_PM') && isTier('B_PM')
              const needsAP = ['ENERGISED_ELECTRICAL','OVERHEAD_LINES'].includes(permit.type)
                && approvalChain.every(a => a.role !== 'Authorised Person')
                && approvalChain.some(a => a.tier === 'B_PM')

              return (
                <>
                  {needsPMSignoff && (
                    <button
                      onClick={() => onAddApproval(user?.fullName ?? 'PM', 'Project Manager — counter-sign', 'B_PM')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-indigo-300 hover:bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg transition-colors">
                      <CheckCircle className="w-3.5 h-3.5" />Counter-sign as Project Manager
                    </button>
                  )}
                  {needsAP && (
                    <button
                      onClick={() => onAddApproval(user?.fullName ?? 'AP', 'Authorised Person — electrical', 'C_WHSO')}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-amber-300 hover:bg-amber-50 text-amber-700 text-xs font-medium rounded-lg transition-colors">
                      <ShieldCheck className="w-3.5 h-3.5" />Sign as Authorised Person (HV/Energised)
                    </button>
                  )}
                  {!needsPMSignoff && !needsAP && (
                    <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                      <CheckCircle className="w-3.5 h-3.5" />Approval chain complete — permit fully authorised
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Overview */}
      {tab === 'overview' && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h3 className="font-semibold text-slate-900">Work description</h3>
          <p className="text-sm text-slate-600">{permit.description}</p>
          <div className="pt-3 border-t">
            <p className="text-xs text-slate-400 mb-1">Location</p>
            <p className="text-sm text-slate-700 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" />{permit.location}</p>
          </div>
        </div>
      )}

      {/* Tab: Workers */}
      {tab === 'workers' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h3 className="font-semibold text-slate-900 text-sm">Workers assigned to this permit</h3>
            <p className="text-xs text-slate-500 mt-0.5">Credentials are verified against worker profiles at time of permit submission</p>
          </div>
          <div className="divide-y divide-slate-100">
            {permit.workers.map(w => (
              <div key={w.employeeId || w.name} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{w.name}</p>
                  <p className="text-xs text-slate-500">{w.role}</p>
                </div>
                <CredentialBadge status={w.credentialStatus} missing={w.missingCredentials} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Pre-start checklist */}
      {tab === 'prestart' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">Pre-start checklist</h3>
              <p className="text-xs text-slate-500 mt-0.5">Must be completed and signed before work commences</p>
            </div>
            {permit.preStartSignedAt && (
              <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Signed {formatDate(permit.preStartSignedAt)}
              </span>
            )}
          </div>
          <div className="divide-y divide-slate-100">
            {preStartItems.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 text-sm text-slate-700">{idx + 1}. {item.label}</div>
                {/* In production: only editable when permit status = APPROVED and current user is assigned worker */}
                <div className="flex gap-1">
                  {(['OK', 'FAIL', 'NA'] as const).map(r => (
                    <button
                      key={r}
                      onClick={() => {
                        setPreStartItems(items => items.map(i => i.id === item.id ? { ...i, result: r } : i))
                      }}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        item.result === r
                          ? r === 'OK' ? 'bg-green-500 text-white border-green-500'
                          : r === 'FAIL' ? 'bg-red-500 text-white border-red-500'
                          : 'bg-slate-400 text-white border-slate-400'
                          : 'border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                    >{r}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {!permit.preStartSignedAt && permit.status === 'APPROVED' && (
            <div className="px-5 py-4 border-t bg-slate-50">
              <button
                onClick={() => { /* In production: POST /api/hse/permits/:id/prestart-sign */ }}
                className="w-full py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                disabled={preStartItems.some(i => i.result === null)}
              >
                Sign pre-start checklist & commence work
              </button>
              {preStartItems.some(i => i.result === null) && (
                <p className="text-xs text-amber-600 text-center mt-2">All items must be answered before signing</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab: Close-out */}
      {tab === 'closeout' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h3 className="font-semibold text-slate-900 text-sm">Close-out checklist</h3>
            <p className="text-xs text-slate-500 mt-0.5">Complete on completion of all work under this permit</p>
          </div>
          <div className="divide-y divide-slate-100">
            {permit.closeOutItems.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 text-sm text-slate-700">{idx + 1}. {item.label}</div>
                <div className="flex gap-1">
                  {(['OK', 'FAIL', 'NA'] as const).map(r => (
                    <button key={r}
                      className={`text-xs px-2 py-1 rounded border ${item.result === r ? r === 'OK' ? 'bg-green-500 text-white border-green-500' : r === 'FAIL' ? 'bg-red-500 text-white border-red-500' : 'bg-slate-400 text-white border-slate-400' : 'border-slate-200 text-slate-400'}`}
                    >{r}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {permit.status === 'IN_PROGRESS' && (
            <div className="px-5 py-4 border-t bg-slate-50 space-y-3">
              <textarea
                value={closeOutNotes}
                onChange={e => setCloseOutNotes(e.target.value)}
                placeholder="Close-out notes (required — describe condition of area on completion)..."
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => onCompleteWork(permit.id, closeOutNotes)}
                disabled={!closeOutNotes.trim()}
                className="w-full py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Sign close-out & finalise permit
              </button>
            </div>
          )}
          {permit.closeOutSignedAt && (
            <div className="px-5 py-3 bg-green-50 text-xs text-green-700 flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5" />
              Closed out by {permit.closeOutSignedByName} · {formatDate(permit.closeOutSignedAt)}
            </div>
          )}
        </div>
      )}

      {/* Reject modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">Reject permit</h3>
            <p className="text-sm text-slate-500">Provide a reason for rejection. The applicant will be notified.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-red-400"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => { onReject(permit.id, rejectReason); setShowRejectModal(false) }}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject permit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// SUBMIT PERMIT MODAL
// ─────────────────────────────────────────────────────────────────
function SubmitPermitModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (p: Partial<Permit>) => void }) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    type: 'EXCAVATION' as PermitType,
    jobId: MOCK_JOBS[0].id,
    zone: '',
    location: '',
    plannedStart: '',
    plannedEnd: '',
    description: '',
    linkedSwmsId: '',
    bydaReference: '',
    energexReference: '',
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const job = MOCK_JOBS.find(j => j.id === form.jobId)
  const relevantSwms = MOCK_SWMS.filter(s => s.jobId === form.jobId && s.status === 'APPROVED')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const num = String(MOCK_PERMITS.length + 1).padStart(3, '0')
    onSubmit({
      permitNumber: `PTW-2026-${num}`,
      type: form.type,
      jobId: form.jobId,
      jobNumber: job?.jobNumber,
      status: 'SUBMITTED',
      zone: form.zone,
      location: form.location,
      plannedStart: form.plannedStart,
      plannedEnd: form.plannedEnd,
      description: form.description,
      linkedSwmsId: form.linkedSwmsId || undefined,
      linkedSwmsNumber: relevantSwms.find(s => s.id === form.linkedSwmsId)?.swmsNumber,
      submittedById: user?.id || '',
      submittedByName: user?.fullName || '',
      submittedAt: new Date().toISOString(),
      workers: [],
      preStartItems: [],
      closeOutItems: [],
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Apply for Permit to Work</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Permit type *</label>
              <select value={form.type} onChange={e => set('type', e.target.value)} className="input w-full text-sm" required>
                {Object.entries(PERMIT_TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Job *</label>
              <select value={form.jobId} onChange={e => set('jobId', e.target.value)} className="input w-full text-sm" required>
                {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Zone / area *</label>
            <input value={form.zone} onChange={e => set('zone', e.target.value)}
              placeholder="e.g. Annerley Substation — Cable Pits A-D" className="input w-full text-sm" required />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Specific location *</label>
            <input value={form.location} onChange={e => set('location', e.target.value)}
              placeholder="e.g. Pit A, cnr Ipswich Rd & Annerley Rd" className="input w-full text-sm" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Planned start *</label>
              <input type="datetime-local" value={form.plannedStart} onChange={e => set('plannedStart', e.target.value)} className="input w-full text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Planned end *</label>
              <input type="datetime-local" value={form.plannedEnd} onChange={e => set('plannedEnd', e.target.value)} className="input w-full text-sm" required />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Work description *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              className="input w-full text-sm resize-none h-20" required
              placeholder="Describe the work to be performed under this permit..." />
          </div>

          {relevantSwms.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Link to SWMS (required for high-risk work)</label>
              <select value={form.linkedSwmsId} onChange={e => set('linkedSwmsId', e.target.value)} className="input w-full text-sm">
                <option value="">— Select SWMS —</option>
                {relevantSwms.map(s => <option key={s.id} value={s.id}>{s.swmsNumber} — {s.title}</option>)}
              </select>
            </div>
          )}

          {(form.type === 'EXCAVATION') && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">BYDA reference number</label>
              <input value={form.bydaReference} onChange={e => set('bydaReference', e.target.value)}
                placeholder="e.g. BYDA-2026-0061234" className="input w-full text-sm" />
            </div>
          )}

          {(form.type === 'ENERGISED_ELECTRICAL' || form.type === 'OVERHEAD_LINES') && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Switching programme (SP) number</label>
              <input value={form.energexReference} onChange={e => set('energexReference', e.target.value)}
                placeholder="e.g. SP-2026-04881" className="input w-full text-sm" />
            </div>
          )}

          {/* Credential warning for selected permit type */}
          <div className={`rounded-lg p-3 text-xs ${PERMIT_TYPE_META[form.type].bg} ${PERMIT_TYPE_META[form.type].color}`}>
            <p className="font-semibold mb-1">Required credentials for this permit type:</p>
            {PERMIT_TYPE_META[form.type].requiredCredentials.length > 0
              ? PERMIT_TYPE_META[form.type].requiredCredentials.map(c => <p key={c}>• {c}</p>)
              : <p>No specific credentials required beyond White Card</p>
            }
            <p className="mt-1 text-slate-500">Worker credentials are verified from their induction profile when you add them to this permit.</p>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">Submit application</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// PHASE 2 — PERMIT TO WORK PDF GENERATOR
// Physical permit document for on-site use.
// Must be printed, signed, and kept on site while works are active.
// Statutory requirement under WHS Act 2011 (Qld) for high-risk work.
// ─────────────────────────────────────────────────────────────────
function generatePermitPDF(permit: any) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDT  = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'medium', timeStyle:'short' }) : '—'
  const typeMeta = PERMIT_TYPE_META[permit.type as PermitType]

  const checkItems = (items: string[], label: string) =>
    items.length > 0 ? `<h2>${label}</h2><ul style="margin:0 0 10px;padding-left:20px">${items.map((x: string)=>`<li style="margin:3px 0;font-size:10pt">${x}</li>`).join('')}</ul>` : ''

  const atmoSection = permit.type === 'CONFINED_SPACE' && permit.o2Level !== undefined ? `
    <h2>Atmospheric readings (confined space)</h2>
    <table style="width:100%;border-collapse:collapse;margin-bottom:12px;font-size:10pt">
      <thead><tr style="background:#1B3A6B;color:#fff"><th style="padding:5px 10px;text-align:left">Gas</th><th style="padding:5px 10px;text-align:center">Reading</th><th style="padding:5px 10px;text-align:center">Acceptable range</th><th style="padding:5px 10px;text-align:center">Pass / Fail</th></tr></thead>
      <tbody>
        <tr><td style="padding:5px 10px">Oxygen (O₂)</td><td style="padding:5px 10px;text-align:center">${permit.o2Level ?? '—'}%</td><td style="padding:5px 10px;text-align:center">19.5–23.5%</td>
          <td style="padding:5px 10px;text-align:center;font-weight:bold;color:${permit.o2Level >= 19.5 && permit.o2Level <= 23.5 ? '#166534' : '#b91c1c'}">${permit.o2Level >= 19.5 && permit.o2Level <= 23.5 ? '✓ PASS' : '⚠ FAIL'}</td></tr>
        <tr style="background:#f9fafb"><td style="padding:5px 10px">Flammable gas (LEL)</td><td style="padding:5px 10px;text-align:center">${permit.lelLevel ?? '0'}%</td><td style="padding:5px 10px;text-align:center">&lt;5% LEL</td>
          <td style="padding:5px 10px;text-align:center;font-weight:bold;color:${(permit.lelLevel ?? 0) < 5 ? '#166534' : '#b91c1c'}">${(permit.lelLevel ?? 0) < 5 ? '✓ PASS' : '⚠ FAIL'}</td></tr>
        <tr><td style="padding:5px 10px">Carbon monoxide (CO)</td><td style="padding:5px 10px;text-align:center">${permit.coLevel ?? '0'} ppm</td><td style="padding:5px 10px;text-align:center">&lt;25 ppm</td>
          <td style="padding:5px 10px;text-align:center;font-weight:bold;color:${(permit.coLevel ?? 0) < 25 ? '#166534' : '#b91c1c'}">${(permit.coLevel ?? 0) < 25 ? '✓ PASS' : '⚠ FAIL'}</td></tr>
        <tr style="background:#f9fafb"><td style="padding:5px 10px">Hydrogen sulphide (H₂S)</td><td style="padding:5px 10px;text-align:center">${permit.h2sLevel ?? '0'} ppm</td><td style="padding:5px 10px;text-align:center">&lt;1 ppm</td>
          <td style="padding:5px 10px;text-align:center;font-weight:bold;color:${(permit.h2sLevel ?? 0) < 1 ? '#166534' : '#b91c1c'}">${(permit.h2sLevel ?? 0) < 1 ? '✓ PASS' : '⚠ FAIL'}</td></tr>
      </tbody>
    </table>
    <p style="font-size:9pt;color:#b91c1c;font-weight:bold">⚠ Atmospheric testing must be repeated every 30 minutes and any time ventilation is interrupted. Work must cease immediately if any reading exceeds acceptable range.</p>` : ''

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Permit to Work — ${permit.permitNumber ?? permit.id}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:16mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.permit-type{font-size:16pt;font-weight:900;padding:6px 16px;border-radius:6px;margin-bottom:6px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 6px;vertical-align:top}.info td:first-child{width:35%;font-weight:bold;color:#555}
.sig-row{display:flex;gap:24px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.validity-box{border:2px solid #1B3A6B;border-radius:6px;padding:12px 16px;margin:12px 0;text-align:center}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:6px}
.APPROVED{background:#dcfce7;color:#166534;border:1.5px solid #86efac}
.SUBMITTED{background:#dbeafe;color:#1d4ed8}
.DRAFT{background:#f3f4f6;color:#374151}
@media print{body{margin:12mm}}
</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right">
    <div class="permit-type ${permit.status}">${typeMeta?.icon ?? ''} ${typeMeta?.label ?? permit.type?.replace(/_/g,' ') ?? 'PERMIT TO WORK'}</div>
    <div style="font-family:monospace;font-size:13pt;font-weight:bold;color:#1B3A6B">${permit.permitNumber ?? permit.id}</div>
    <div style="font-size:9pt;color:#888">Status: ${permit.status?.replace(/_/g,' ') ?? '—'}</div>
  </div>
</div>

<div class="info"><table>
  <tr><td>Job / project</td><td><strong>${permit.jobNumber ?? '—'}</strong></td>
      <td>Location</td><td>${permit.location ?? '—'}</td></tr>
  <tr><td>Requested by</td><td>${permit.requestedByName ?? '—'}</td>
      <td>Date requested</td><td>${fmtDT(permit.requestedAt)}</td></tr>
  <tr><td>Approved by</td><td>${permit.approvedByName ?? 'Pending'}</td>
      <td>Date approved</td><td>${fmtDT(permit.approvedAt)}</td></tr>
  ${permit.linkedSwmsRef ? `<tr><td>Linked SWMS</td><td colspan="3">${permit.linkedSwmsRef}</td></tr>` : ''}
</table></div>

<div class="validity-box">
  <div style="font-size:9pt;color:#555;margin-bottom:4px">PERMIT VALIDITY PERIOD</div>
  <div style="font-size:14pt;font-weight:bold;color:#1B3A6B">
    ${permit.validFrom ? fmtDT(permit.validFrom) : '—'} &rarr; ${permit.validTo ? fmtDT(permit.validTo) : '—'}
  </div>
  <div style="font-size:9pt;color:#b91c1c;margin-top:4px;font-weight:bold">This permit is NOT valid outside the times shown above</div>
</div>

<h2>Scope of work</h2>
<div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:48px;font-size:10.5pt">${permit.scopeOfWork ?? permit.description ?? 'Scope of work not recorded.'}</div>

${checkItems(permit.requiredCredentials ?? [], 'Required credentials / licences')}
${checkItems(permit.safetyRequirements ?? [], 'Safety requirements')}
${atmoSection}

<h2>By-da / service authorities</h2>
<div class="info"><table>
  <tr><td>BYDA reference</td><td>${permit.bydaRef ?? '—'}</td>
      <td>Energex / authority ref</td><td>${permit.energexRef ?? '—'}</td></tr>
</table></div>

<h2>Sign-on — workers authorised to work under this permit</h2>
<table style="width:100%;border-collapse:collapse;font-size:10pt">
  <thead><tr style="background:#1B3A6B;color:#fff">
    <th style="padding:6px 10px;text-align:left">Name</th>
    <th style="padding:6px 10px;text-align:left">Trade / role</th>
    <th style="padding:6px 10px;text-align:center">Time in</th>
    <th style="padding:6px 10px;text-align:center">Time out</th>
    <th style="padding:6px 10px">Signature</th>
  </tr></thead>
  <tbody>
    ${[1,2,3,4].map((_,i)=>`<tr style="background:${i%2===0?'#fff':'#f9fafb'}"><td style="padding:22px 10px;border-bottom:1px solid #e5e7eb"></td><td style="padding:22px 10px;border-bottom:1px solid #e5e7eb"></td><td style="padding:22px 10px;border-bottom:1px solid #e5e7eb;text-align:center"></td><td style="padding:22px 10px;border-bottom:1px solid #e5e7eb;text-align:center"></td><td style="padding:22px 10px;border-bottom:1px solid #aaa"></td></tr>`).join('')}
  </tbody>
</table>

<div style="margin-top:14px;padding:10px 14px;background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;font-size:9.5pt">
  <strong>⚠ PERMIT CONDITIONS:</strong> This permit must be kept on site at the work location at all times while work is in progress. Work must cease immediately if site conditions change materially or a safety incident occurs. The issuing WHSO must be notified immediately of any changes. The permit must be surrendered and formally closed on completion of works.
</div>

<h2>Permit close-out</h2>
<div class="sig-row">
  <div class="sig-block">
    <div style="font-size:9pt;color:#555;margin-bottom:4px">Completed / surrendered at: _______________</div>
    <div class="sig-line"></div><div class="sig-lbl">Permit holder — name &amp; date</div>
  </div>
  <div class="sig-block">
    <div style="font-size:9pt;color:#555;margin-bottom:4px">Permit closed at: _______________</div>
    <div class="sig-line"></div><div class="sig-lbl">WHSO / PM close-out — name &amp; date</div>
  </div>
</div>
<div class="footer">WHS Act 2011 (Qld) · WHS Regulation 2011 (Qld) Schedule 2 · ${permit.permitNumber ?? permit.id} · MCB Civil Electrical · Generated ${now}</div>
</body></html>`

  const win = window.open('', '_blank', 'width=960,height=800')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print permit'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}
export function Permits() {
  const { isTier } = useAuthStore()
  const [permits, setPermits]       = useState(MOCK_PERMITS as Permit[])
  const [apiSeeded, setApiSeeded]   = useState(false)
  const [selected, setSelected]     = useState<Permit | null>(null)
  const [showSubmit, setShowSubmit] = useState(false)
  const [search, setSearch]         = useState('')
  const [statusFilter, setStatusFilter] = useState<PermitStatus | 'ALL'>('ALL')
  const [typeFilter, setTypeFilter]     = useState<PermitType | 'ALL'>('ALL')
  // Multi-approver chains: permitId → approval records
  const [approvalChains, setApprovalChains] = useState<
    Record<string, { name: string; role: string; signedAt: string; tier: string }[]>
  >({})

  const addApproval = (permitId: string, name: string, role: string, tier: string) => {
    setApprovalChains(prev => ({
      ...prev,
      [permitId]: [...(prev[permitId] ?? []), { name, role, tier, signedAt: new Date().toISOString() }],
    }))
  }

  // Live permits from Neon — seeded into state on first load
  const { data: apiPermits = [], isLoading: permitsLoading } = useQuery({
    queryKey: ['permits'],
    queryFn:  () => fetchPermits(),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (apiPermits.length > 0 && !apiSeeded) {
      const apiIds = new Set(apiPermits.map((p: any) => p.id))
      setPermits([
        ...(apiPermits as unknown as Permit[]),
        ...(MOCK_PERMITS as Permit[]).filter(p => !apiIds.has(p.id)),
      ])
      setApiSeeded(true)
    }
  }, [apiPermits, apiSeeded])

  const isWHSO      = isTier('C_WHSO')               // WHS Officer — primary permit approver
  const canApprove  = isTier('B_PM') || isWHSO       // WHSO and PM/Admin can approve
  const canSubmit   = true                            // All tiers can submit

  const filtered = permits.filter(p => {
    const matchSearch = !search || p.permitNumber.toLowerCase().includes(search.toLowerCase()) ||
      p.zone.toLowerCase().includes(search.toLowerCase()) || p.jobNumber.toLowerCase().includes(search.toLowerCase())
    const matchStatus = statusFilter === 'ALL' || p.status === statusFilter
    const matchType   = typeFilter === 'ALL' || p.type === typeFilter
    return matchSearch && matchStatus && matchType
  })

  const stats = {
    pending:    permits.filter(p => p.status === 'SUBMITTED').length,
    active:     permits.filter(p => p.status === 'IN_PROGRESS').length,
    approved:   permits.filter(p => p.status === 'APPROVED').length,
    credIssues: permits.filter(p => p.workers.some(w => w.credentialStatus !== 'OK')).length,
  }

  const handleApprove = (id: string) => {
    setPermits(ps => ps.map(p => p.id === id ? { ...p, status: 'APPROVED', approvedByName: 'Sarah Kim (WHSO)', approvedAt: new Date().toISOString() } : p))
    setSelected(null)
  }

  const handleReject = (id: string, reason: string) => {
    setPermits(ps => ps.map(p => p.id === id ? { ...p, status: 'REJECTED', rejectionReason: reason } : p))
    setSelected(null)
  }

  const handleStartWork = (id: string) => {
    setPermits(ps => ps.map(p => p.id === id ? { ...p, status: 'IN_PROGRESS', actualStart: new Date().toISOString() } : p))
    if (selected?.id === id) setSelected(p => p ? { ...p, status: 'IN_PROGRESS' } : p)
  }

  const handleCompleteWork = (id: string, notes: string) => {
    setPermits(ps => ps.map(p => p.id === id ? { ...p, status: 'COMPLETED', actualEnd: new Date().toISOString(), closeOutNotes: notes, closeOutSignedByName: 'Current User', closeOutSignedAt: new Date().toISOString() } : p))
    setSelected(null)
  }

  const handleSubmit = (partial: Partial<Permit>) => {
    const newPermit = { ...partial, id: `ptw_${Date.now()}`, workers: [], preStartItems: [], closeOutItems: [] } as Permit
    setPermits(ps => [newPermit, ...ps])
  }

  if (selected) {
    return (
      <PermitDetail
        permit={selected}
        onBack={() => setSelected(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onStartWork={handleStartWork}
        onCompleteWork={handleCompleteWork}
        approvalChain={approvalChains[selected.id] ?? []}
        onAddApproval={(name, role, tier) => addApproval(selected.id, name, role, tier)}
      />
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Permits to Work</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage permit applications, approvals, and active work authorities
            {apiSeeded && apiPermits.length > 0 && <span className="ml-2 text-blue-600 font-medium">· {apiPermits.length} live from Neon</span>}
          </p>
        </div>
        {canSubmit && (
          <button onClick={() => setShowSubmit(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> Apply for permit
          </button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Awaiting approval', value: stats.pending, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Approved (not started)', value: stats.approved, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Active on site', value: stats.active, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Credential issues', value: stats.credIssues, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border border-white`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search permit number, zone, job..."
            className="input pl-9 w-full text-sm" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="input text-sm">
          <option value="ALL">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="input text-sm">
          <option value="ALL">All types</option>
          {Object.entries(PERMIT_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
      </div>

      {/* Permit list */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
            <p className="font-medium">No permits found</p>
            <p className="text-sm mt-1">Try adjusting your filters or apply for a new permit</p>
          </div>
        )}
        {filtered.map(p => (
          <PermitCard key={p.id} permit={p} onClick={() => setSelected(p)} />
        ))}
      </div>

      {showSubmit && <SubmitPermitModal onClose={() => setShowSubmit(false)} onSubmit={handleSubmit} />}
    </div>
  )
}
